/**
 * THE FILE — tonal ground.
 *
 * A band is the product's only way of saying "this part of the page is
 * different". It changes the ground under the words instead of drawing a line
 * around them, so the reader feels the grouping without counting borders. The
 * tints are close to the canvas on purpose: the band separates, it does not
 * shout.
 */
import { clsx } from "clsx";
import type { ReactNode } from "react";
import type { MarkerTone } from "./tone.js";
import { Label } from "./text.js";

export interface BandProps {
  children: ReactNode;
  /**
   * The state tint. `progress` and `optional` deliberately fall through to the
   * neutral band: neither is a condition worth colouring the ground for.
   */
  tone?: MarkerTone | undefined;
  className?: string | undefined;
}

/** Ground.band — the ONE thing happening now, or a group header. */
export function Band({ children, tone, className }: BandProps) {
  const ground =
    tone === "attention" ? "bg-att-tint"
      : tone === "conflict" ? "bg-con-tint"
        : tone === "established" ? "bg-est-tint"
          : "bg-band";
  return <div className={clsx(ground, className)}>{children}</div>;
}

export interface GroupBandProps {
  label: ReactNode;
  /** A count, set tabular so a column of groups reads as a column of numbers. */
  count?: number | undefined;
  /** What the group means, in the product's own words — never a tooltip. */
  hint?: ReactNode;
  className?: string | undefined;
}

/**
 * A group header inside a ruled list: caps label · count · hint, on a band.
 *
 * The hint sits on the same line rather than under it because a group heading
 * that takes two lines starts to look like a section of its own, and the rows
 * beneath it are the content — the heading is only telling you what they have
 * in common.
 */
export function GroupBand({ label, count, hint, className }: GroupBandProps) {
  return (
    <div className={clsx("mt-5 flex items-baseline gap-2.5 bg-band px-3 py-1.5", className)}>
      <Label className="text-ink">{label}</Label>
      {typeof count === "number" && (
        <span className="text-[13px] text-stone [font-variant-numeric:tabular-nums]">{count}</span>
      )}
      {hint && <span className="text-[13px] text-stone">· {hint}</span>}
    </div>
  );
}
